import { ListItemIcon, ListItemText } from '@mui/material';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { s } from '@/theme';
import { r } from '@/theme/tokens/radii';
import {
  AddIcon,
  Box,
  ChevronRightIcon,
  Divider,
  MenuItem,
  MenuList,
  Popover,
  Text,
  Tooltip,
} from '@/ui/primitives';
import { Stack } from '../layout/Stack';
import type { ReactElement, ReactNode } from 'react';

export type SidebarItem = {
  id: string;
  label: string;
  href: string;
  icon?: ReactElement;
  leadingAdornment?: ReactElement;
  active?: boolean;
  disabled?: boolean;
  disabledTone?: 'muted' | 'default';
  preload?: () => Promise<void> | null;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  popover?: {
    title?: string;
    items: Array<{
      id: string;
      label: string;
      onClick?: () => void;
      leadingAdornment?: ReactElement;
      helperText?: string;
    }>;
  };
  rightAdornment?: ReactElement;
};

export type SidebarSection = { title: string; items: SidebarItem[] };

type SidebarNavProps = {
  sections: SidebarSection[];
  footerItems?: SidebarItem[];
  footerTitle?: string;
  footerContent?: ReactNode;
};

export const SidebarNav = ({
  sections,
  footerItems,
  footerTitle,
  footerContent,
}: SidebarNavProps): ReactElement => {
  const navigate = useNavigate();
  const sidebarItemMinHeight = `calc(${s('2xl')} + ${s('lg')})`;
  const sidebarItemFontSize = '15px';
  const sidebarItemFontWeight = 500;
  const sidebarSectionLabelFontSize = '13px';
  const sidebarSectionLabelBottomSpacing = s('md');
  const leadingAdornmentSpacing = `calc(${s('sm')} + ${s('md')})`;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activePopover, setActivePopover] = useState<SidebarItem['popover'] | null>(null);
  const resolvedFooterItems = footerItems ?? [];
  const hasFooterItems = resolvedFooterItems.length > 0;

  const handleClose = (): void => {
    setAnchorEl(null);
    setActivePopover(null);
  };

  const renderSidebarItem = (item: SidebarItem): ReactElement => (
    <Box
      key={item.id}
      component={item.popover ? 'a' : RouterLink}
      {...(item.popover ? { href: item.href } : { to: item.href })}
      aria-disabled={item.disabled ? true : undefined}
      data-has-popover={item.popover ? 'true' : undefined}
      onMouseEnter={() => {
        if (!item.disabled) {
          void item.preload?.();
        }
      }}
      onFocus={() => {
        if (!item.disabled) {
          void item.preload?.();
        }
      }}
      onClick={(event) => {
        if (item.disabled) {
          event.preventDefault();
          return;
        }
        if (!item.popover && item.preload && item.href.startsWith('/')) {
          const isPrimaryButton = event.button === 0;
          const isModifiedClick = event.metaKey || event.altKey || event.ctrlKey || event.shiftKey;
          if (isPrimaryButton && !isModifiedClick) {
            event.preventDefault();
            const preloadResult = item.preload();
            if (preloadResult === null) {
              void navigate(item.href);
              return;
            }
            void preloadResult
              .catch(() => null)
              .finally(() => {
                void navigate(item.href);
              });
            return;
          }
        }
        item.onClick?.(event);
        if (item.popover) {
          event.preventDefault();
          setAnchorEl(event.currentTarget);
          setActivePopover(item.popover);
        }
      }}
      aria-current={item.active ? 'page' : undefined}
      sx={(theme) => ({
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        textAlign: 'left',
        paddingX: s('lg'),
        paddingY: s('sm'),
        borderRadius: s('sm'),
        textDecoration: 'none',
        color: item.active
          ? theme.palette.getContrastText(theme.app.colors.primary)
          : item.disabled && item.disabledTone !== 'default'
            ? theme.palette.text.secondary
            : theme.app.colors.text,
        fontWeight: sidebarItemFontWeight,
        fontSize: sidebarItemFontSize,
        lineHeight: theme.typography.body.lineHeight,
        minHeight: sidebarItemMinHeight,
        backgroundColor: item.active ? theme.app.colors.primary : 'transparent',
        cursor: item.disabled ? 'default' : 'pointer',
        pointerEvents: item.disabled ? 'none' : 'auto',
        transition: 'background-color 120ms ease, color 120ms ease',
        '&:hover': item.active
          ? undefined
          : {
              backgroundColor:
                theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)',
              color: theme.app.colors.text,
            },
        '&:focus-visible': {
          outline: `2px solid ${theme.app.state.focus}`,
          outlineOffset: 2,
        },
      })}
    >
      {item.leadingAdornment ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            mr: leadingAdornmentSpacing,
          }}
        >
          {item.leadingAdornment}
        </Box>
      ) : null}
      {item.icon ? (
        <ListItemIcon
          sx={(theme) => ({
            minWidth: '20px !important',
            width: 20,
            mr: `calc(${s('xs')} + ${s('sm')})`,
            color: item.active
              ? theme.palette.getContrastText(theme.app.colors.primary)
              : item.disabled && item.disabledTone !== 'default'
                ? theme.palette.text.secondary
                : theme.app.colors.text,
            flexShrink: 0,
            opacity: item.active ? 0.92 : 0.8,
            '& .MuiSvgIcon-root': {
              fontSize: '18px',
            },
          })}
        >
          {item.icon}
        </ListItemIcon>
      ) : null}
      <Text
        role="body"
        component="span"
        sx={{
          minWidth: 0,
          flex: '1 1 auto',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {item.label}
      </Text>
      {item.popover || item.rightAdornment ? (
        <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: s('xs') }}>
          {item.rightAdornment}
          {item.popover ? <ChevronRightIcon fontSize="small" /> : null}
        </Box>
      ) : null}
    </Box>
  );

  return (
    <Box
      component="nav"
      aria-label="Primary"
      sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      {sections.map((section, index) => (
        <Box key={section.title} sx={{ marginTop: index === 0 ? s('md') : s('xl') }}>
          <Text
            role="label"
            component="div"
            sx={{
              color: 'text.secondary',
              display: 'block',
              paddingBottom: sidebarSectionLabelBottomSpacing,
              paddingX: s('md'),
              fontSize: sidebarSectionLabelFontSize,
            }}
          >
            {section.title}
          </Text>
          <Stack gap="sm" sx={{ marginBottom: s('sm') }}>
            {section.items.map(renderSidebarItem)}
          </Stack>
        </Box>
      ))}
      {footerTitle ? (
        <Text
          role="label"
          sx={{
            color: 'text.secondary',
            marginTop: s('xl'),
            marginBottom: s('sm'),
            fontSize: sidebarSectionLabelFontSize,
          }}
        >
          {footerTitle}
        </Text>
      ) : null}
      {footerContent}
      <Box sx={{ marginTop: 'auto', paddingTop: s('2xl') }}>
        {hasFooterItems ? (
          <Stack gap="sm" sx={{ marginBottom: s('sm') }}>
            <Text
              role="label"
              component="div"
              sx={{
                color: 'text.secondary',
                paddingX: s('md'),
                paddingBottom: sidebarSectionLabelBottomSpacing,
                fontSize: sidebarSectionLabelFontSize,
              }}
            >
              Admin
            </Text>
            {resolvedFooterItems.map(renderSidebarItem)}
          </Stack>
        ) : null}
      </Box>
      <Popover
        open={Boolean(anchorEl && activePopover)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        paperSx={(theme) => ({
          marginLeft: s('sm'),
          width: 'fit-content',
          minWidth: 140,
          maxWidth: 'calc(100vw - 24px)',
          border: `1px solid ${theme.app.colors.border}`,
          borderRadius: `${r('card')} !important`,
          backgroundColor: theme.app.colors.surface,
          backgroundImage: 'none',
          boxShadow: 'none',
          '--Paper-overlay': 'none',
          overflow: 'hidden',
          '& .MuiMenuItem-root': {
            fontSize: sidebarItemFontSize,
            lineHeight: 1.2,
          },
          '& .MuiMenuItem-root .MuiListItemText-primary': {
            fontSize: sidebarItemFontSize,
            lineHeight: 1.2,
          },
        })}
      >
        {(() => {
          const addActionItem =
            activePopover?.items.find((item) => item.id.startsWith('add-')) ?? null;
          const selectableItems =
            activePopover?.items.filter((item) => !item.id.startsWith('add-')) ?? [];
          const allProjectsItem = selectableItems.find((item) => item.id === 'all-projects');
          const otherSelectableItems = selectableItems.filter((item) => item.id !== 'all-projects');
          const orderedSelectableItems = allProjectsItem
            ? [allProjectsItem, ...otherSelectableItems]
            : otherSelectableItems;

          return (
            <>
              {activePopover?.title ? (
                <>
                  <Box sx={{ px: s('lg'), pt: s('md'), pb: s('md') }}>
                    <Text role="body" sx={{ fontSize: sidebarItemFontSize, fontWeight: 600 }}>
                      {activePopover.title}
                    </Text>
                  </Box>
                  <Divider />
                </>
              ) : null}
              <MenuList dense disablePadding sx={{ pt: s('none'), pb: s('none') }}>
                {orderedSelectableItems.map((popoverItem) => (
                  <MenuItem
                    key={popoverItem.id}
                    onClick={() => {
                      popoverItem.onClick?.();
                      handleClose();
                    }}
                    sx={(theme) => ({
                      px: s('lg'),
                      py: s('md'),
                      borderRadius: r('none'),
                      typography: 'body',
                      lineHeight: 1.2,
                      color: theme.app.colors.text,
                      gap: 0,
                    })}
                  >
                    {popoverItem.leadingAdornment ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          flexShrink: 0,
                          mr: leadingAdornmentSpacing,
                        }}
                      >
                        {popoverItem.leadingAdornment}
                      </Box>
                    ) : null}
                    <Box sx={{ minWidth: 0 }}>
                      <Text
                        role="body"
                        sx={{
                          display: 'block',
                          fontSize: sidebarItemFontSize,
                          fontWeight: sidebarItemFontWeight,
                          lineHeight: 1.2,
                        }}
                      >
                        {popoverItem.label}
                      </Text>
                      {popoverItem.helperText ? (
                        <Text
                          role="caption"
                          color="textSecondary"
                          sx={{ display: 'block', mt: s('xxs') }}
                        >
                          {popoverItem.helperText}
                        </Text>
                      ) : null}
                    </Box>
                  </MenuItem>
                ))}
                {addActionItem ? (
                  <Tooltip
                    title={addActionItem.onClick ? '' : 'Project Limit Reached'}
                    placement="right"
                    arrow
                    slotProps={{
                      tooltip: {
                        sx: { fontSize: (theme) => theme.typography.body.fontSize },
                      },
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        if (!addActionItem.onClick) return;
                        addActionItem.onClick();
                        handleClose();
                      }}
                      sx={(theme) => ({
                        px: s('lg'),
                        py: s('md'),
                        borderRadius: r('none'),
                        display: 'flex',
                        alignItems: 'center',
                        gap: s('sm'),
                        color: addActionItem.onClick
                          ? theme.app.colors.text
                          : theme.palette.text.secondary,
                        cursor: addActionItem.onClick ? 'pointer' : 'default',
                        opacity: addActionItem.onClick ? 1 : 0.7,
                        '& .MuiListItemIcon-root': {
                          minWidth: '20px !important',
                          width: 20,
                          mr: 0,
                          color: 'inherit',
                          flexShrink: 0,
                        },
                        '& .MuiListItemText-root': { m: 0 },
                        '& .MuiListItemText-primary': { lineHeight: 1.2 },
                      })}
                    >
                      <ListItemIcon>
                        <AddIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={addActionItem.label} />
                    </MenuItem>
                  </Tooltip>
                ) : null}
              </MenuList>
            </>
          );
        })()}
      </Popover>
    </Box>
  );
};
